import {FormControl, FormField, Separator, Tooltip} from 'platform/components';
import {Box, HStack, Show, Space, VStack, Icon} from 'platform/foundation';

import {UseFormReturn, useWatch} from 'react-hook-form';
import {useLocation, useParams} from 'react-router-dom';

import {isNil} from 'ramda';
import {isNumber} from 'ramda-adjunct';

import {
  useGetDictionaryQuery,
  useGetServiceCaseTypesQuery,
  useGetServiceOrderTypesQuery,
  useGetSettingsServiceOrderVariantsAllQuery,
} from '@dms/api/workshop';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {SettingsFooter, SettingsSection, DefaultCustomer} from '@dms/settings/shared';

import {useNavigate} from 'shared/web';

import {FormType} from '../../types/ServiceOrderVariant';
import {getServiceOrderVariantsRoutes} from '../../utils/getServiceOrderVariantsRoutes';

interface GeneralTabProps {
  control: FormControl<FormType>;
  formApi: UseFormReturn<FormType>;
}

export function GeneralTab(props: GeneralTabProps) {
  const {id} = useParams();
  const {pathname} = useLocation();
  const navigate = useNavigate();
  const serviceOrderVariantsRoutes = getServiceOrderVariantsRoutes(pathname);

  const {data: serviceOrderVariants} = useGetSettingsServiceOrderVariantsAllQuery();

  const {data: priceTypes, isLoading: isPriceTypesLoading} = useGetDictionaryQuery({
    category: 'SVCPRICETYPEMATERIAL',
  });
  const {data: rateTypes, isLoading: isRateTypesLoading} = useGetDictionaryQuery({
    category: 'SVCRATETYPE',
  });
  const {data: orderTypes, isLoading: isOrderTypesLoading} = useGetServiceOrderTypesQuery();
  const {data: serviceCaseTypes, isLoading: isServiceCaseTypeLoading} =
    useGetServiceCaseTypesQuery();

  const getDefaultServiceVariantByServiceCaseType = (serviceCaseTypeId: string) =>
    serviceOrderVariants?.find(
      (variant) =>
        variant?.serviceCaseTypeId === serviceCaseTypeId && variant.isDefaultForServiceCaseType
    );

  const serviceCaseTypeIdValue = useWatch({
    control: props.formApi.control,
    name: 'general.serviceCaseTypeId',
  });

  const defaultServiceVariant = getDefaultServiceVariantByServiceCaseType(
    serviceCaseTypeIdValue ?? ''
  );

  const isDefaultServiceVariant = !!(id && defaultServiceVariant?.id === id);

  const handleServiceCaseTypeChange = (value: string | number | null) => {
    if (isNumber(value) || isNil(value)) {
      return;
    }

    const selectedDefaultServiceVariant = getDefaultServiceVariantByServiceCaseType(value);

    props.formApi.setValue(
      'general.isDefaultForServiceCaseType',
      !!(!selectedDefaultServiceVariant || (id && selectedDefaultServiceVariant.id === id))
    );
  };

  const customerIdValue = useWatch({
    control: props.control,
    name: 'general.defaultCustomerId',
  });

  return (
    <>
      <SettingsSection>
        <VStack spacing={4}>
          <FormField
            control={props.control}
            label={i18n.t('general.labels.name')}
            name="general.name"
            type="text"
            isRequired
            data-testid={testIds.settings.serviceOrderVariantsDetail('name')}
          />
          <HStack spacing={4}>
            <Box flex={1}>
              <FormField
                control={props.control}
                label={i18n.t('entity.settings.labels.serviceCaseType')}
                name="general.serviceCaseTypeId"
                type="choice"
                isLoading={isServiceCaseTypeLoading}
                options={
                  serviceCaseTypes?.map((profile) => ({
                    label: profile?.name,
                    value: profile?.id,
                  })) ?? []
                }
                onChange={handleServiceCaseTypeChange}
                isRequired
                isNotClearable
                data-testid={testIds.settings.serviceOrderVariantsDetail('serviceCaseTypeId')}
              />
            </Box>
            <Box flex={1}>
              <Space vertical={6} />
              <FormField
                control={props.control}
                label={i18n.t('entity.settings.labels.defaultServiceCaseType')}
                name="general.isDefaultForServiceCaseType"
                type="switch"
                isDisabled={!defaultServiceVariant || isDefaultServiceVariant}
                data-testid={testIds.settings.serviceOrderVariantsDetail('defaultServiceCaseType')}
              />
            </Box>
          </HStack>
          <Separator spacing={0} />
          <HStack spacing={4}>
            <Box flex={1}>
              <FormField
                control={props.control}
                label={i18n.t('entity.serviceOrder.labels.serviceOrderType')}
                name="general.serviceOrderTypeId"
                type="choice"
                isLoading={isOrderTypesLoading}
                options={
                  orderTypes?.map((orderType) => ({
                    label: orderType?.name,
                    value: orderType?.id,
                  })) ?? []
                }
                isRequired
                data-testid={testIds.settings.serviceOrderVariantsDetail('orderType')}
              />
            </Box>
            <Box flex={1}>
              <Space vertical={6} />
              <FormField
                control={props.control}
                label={i18n.t('general.labels.unitPriceWithVat')}
                name="general.isUnitPriceWithVat"
                type="switch"
                data-testid={testIds.settings.serviceOrderVariantsDetail('unitPriceWithVat')}
              />
            </Box>
          </HStack>
          <HStack spacing={4}>
            <Box flex={1}>
              <FormField
                control={props.control}
                label={i18n.t('entity.serviceOrder.labels.materialPriceType')}
                name="general.priceType"
                type="choice"
                options={priceTypes ?? []}
                isLoading={isPriceTypesLoading}
                isRequired
                data-testid={testIds.settings.serviceOrderVariantsDetail('materialPriceType')}
              />
            </Box>
            <Box flex={1}>
              <HStack spacing={4}>
                <Box flex={1}>
                  <FormField
                    control={props.control}
                    label={i18n.t('entity.serviceOrder.labels.materialPriceRatio')}
                    name="general.priceTypeRatio"
                    type="number"
                    decimalPlaces={2}
                    isRequired
                    suffix={i18n.t('general.metric.percentage')}
                    data-testid={testIds.settings.serviceOrderVariantsDetail('materialRatio')}
                  />
                </Box>
                <Box flex={1}>
                  <FormField
                    control={props.control}
                    label={i18n.t('entity.serviceOrder.labels.minimumMaterialMargin')}
                    name="general.minimalMaterialMargin"
                    type="number"
                    decimalPlaces={2}
                    suffix={i18n.t('general.metric.percentage')}
                    data-testid={testIds.settings.serviceOrderVariantsDetail('materialMargin')}
                  />
                </Box>
              </HStack>
            </Box>
          </HStack>
          <HStack spacing={4}>
            <Box flex={1}>
              <FormField
                control={props.control}
                label={i18n.t('entity.order.labels.workRateType')}
                name="general.rateType"
                type="choice"
                options={rateTypes ?? []}
                isLoading={isRateTypesLoading}
                isRequired
                data-testid={testIds.settings.serviceOrderVariantsDetail('workRateType')}
              />
            </Box>
            <Box flex={1}>
              <HStack spacing={4}>
                <Box flex={1}>
                  <FormField
                    control={props.control}
                    label={i18n.t('entity.serviceOrder.labels.workRatio')}
                    name="general.rateTypeRatio"
                    type="number"
                    decimalPlaces={2}
                    isRequired
                    suffix={i18n.t('general.metric.percentage')}
                    data-testid={testIds.settings.serviceOrderVariantsDetail('workRatio')}
                  />
                </Box>
                <Box flex={1}>
                  <FormField
                    control={props.control}
                    label={i18n.t('entity.serviceOrder.labels.minimumWorkMargin')}
                    name="general.minimalWorkMargin"
                    type="number"
                    decimalPlaces={2}
                    suffix={i18n.t('general.metric.percentage')}
                    data-testid={testIds.settings.serviceOrderVariantsDetail('workMargin')}
                  />
                </Box>
              </HStack>
            </Box>
          </HStack>
          <Show whenFeatureEnabled={featureFlags.AFS_WAREHOUSE_ACL_SCOPES}>
            <HStack spacing={1} align="center">
              <FormField
                control={props.control}
                label={i18n.t('entity.warehouse.labels.restrictSaleBelowPurchasePrice')}
                name="general.restrictSaleBelowCost"
                type="switch"
                data-testid={testIds.settings.serviceOrderVariantsDetail('restrictSaleBelowCost')}
              />
              <Tooltip
                description={i18n.t(
                  'entity.warehouse.labels.restrictSaleBelowPurchasePriceTooltip'
                )}
              >
                <Icon value="action/help" color="text.secondary" size={4} tabIndex={0} />
              </Tooltip>
            </HStack>
          </Show>
          <HStack spacing={4}>
            <Box flex={1}>
              <FormField
                control={props.control}
                label={i18n.t('general.labels.suffix')}
                name="general.suffix"
                type="text"
                isRequired
                data-testid={testIds.settings.serviceOrderVariantsDetail('suffix')}
              />
            </Box>
            <Space fillAvailable />
          </HStack>
          <FormField
            control={props.control}
            label={i18n.t('general.labels.note')}
            name="general.note"
            type="textarea"
            data-testid={testIds.settings.serviceOrderVariantsDetail('note')}
          />
          <FormField
            control={props.control}
            label={i18n.t('entity.serviceOrder.labels.isCancelReasonRequired')}
            name="general.isCancelReasonRequired"
            type="switch"
            data-testid={testIds.settings.serviceOrderVariantsDetail('isCancelReasonRequired')}
          />
        </VStack>
      </SettingsSection>
      <Separator spacing={6} />
      <VStack spacing={6}>
        <DefaultCustomer
          customerId={customerIdValue}
          onCustomerChange={(customerId) =>
            props.formApi.setValue('general.defaultCustomerId', customerId)
          }
          isDisabled={props.formApi.formState.disabled}
          data-testid={testIds.settings.serviceOrderVariantsDetail('defaultCustomer')}
        />
        <Show when={customerIdValue}>
          <FormField
            control={props.control}
            label={i18n.t('entity.serviceOrder.labels.changeDefaultCustomerIsAllowed')}
            name="general.isChangeableDefaultCustomer"
            type="switch"
            data-testid={testIds.settings.serviceOrderVariantsDetail('isChangeableDefaultCustomer')}
          />
        </Show>
      </VStack>
      <Space vertical={20} />
      <SettingsFooter
        actions={[
          {
            type: 'button',
            variant: 'secondary',
            title: i18n.t('general.actions.discardChanges'),
            onClick: () => navigate(serviceOrderVariantsRoutes.overview),
          },
          {
            type: 'form-button',
            control: props.control,
            buttonType: 'submit',
            variant: 'primary',
            title: i18n.t('general.actions.saveChanges'),
          },
        ]}
        data-testid={testIds.settings.serviceOrderVariantsDetail('footer')}
      />
    </>
  );
}
